import { ActionModule, ActionModuleEmployeeResponse, ActionModuleRequest, ActionModuleAdminListResponse } from "../types/actionModule/actionModule";
import { ActionModuleInstanceResponse } from "../types/actionModule/instance/actionModuleInstance";
import { Dayjs } from "dayjs";
import { PaginationParameters, PaginationResult, toQueryParameters } from "./Pagination";
import { CallResult, doApiDelete, doApiGet, doApiPatch, doApiPost } from "./Requests";

export const actionModulesApi = {
  getActions: (): Promise<CallResult<ActionModule[]>> =>
    doApiGet<ActionModule[]>("/api/companies/current/actionmodules"),

  getActionsFilteredByVisibility: (
      isCompanyWideAssigned: boolean,
      groupIds: number[],
      employeeUserIds: string[]
  ): Promise<CallResult<ActionModule[]>> => {
    const params = new URLSearchParams();
    params.append("isCompanyWideAssigned", String(isCompanyWideAssigned));
    groupIds.forEach(id => params.append("groupIds", String(id)));
    employeeUserIds.forEach(id => params.append("employeeUserIds", id));

    return doApiGet<ActionModule[]>(`/api/companies/current/actionmodules/filtered?${params.toString()}`);
  },

  createAction: (actionData: any): Promise<CallResult<ActionModule>> =>
    doApiPost<any, ActionModule>("/api/companies/current/actionmodules", actionData),

  editAction: (id: number, actionData: ActionModuleRequest, metadataOnly: boolean, validateOnly: boolean): Promise<CallResult<void>> =>
    doApiPatch<ActionModuleRequest, void>(`/api/companies/current/actionmodules/${id}?metadataOnly=${metadataOnly}&validateOnly=${validateOnly}`, actionData),

  getAction: (id: string): Promise<CallResult<ActionModule>> =>
    doApiGet<ActionModule>(`/api/companies/current/actionmodules/${id}`),

  updateActionModuleOrder: async (orderedIds: number[]) => {
    return await doApiPatch(`/api/companies/current/actionmodules/order`, orderedIds)
  },

  getEmployeesForActionModule: (
    actionModuleId: number,
    pagination: PaginationParameters,
    startDate: Dayjs,
    endDate: Dayjs,
  ): Promise<CallResult<PaginationResult<ActionModuleEmployeeResponse>>> =>
    doApiGet<PaginationResult<ActionModuleEmployeeResponse>>(
      `/api/companies/current/actionmodules/${actionModuleId}/employees?${toQueryParameters(pagination)}&startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
    ),

  getActionModuleInstances: (
    actionModuleId: number,
    employeeId: string,
    startDate: Dayjs,
    endDate: Dayjs,
  ): Promise<CallResult<ActionModuleInstanceResponse[]>> =>
    doApiGet<ActionModuleInstanceResponse[]>(
      `/api/companies/current/actionmodules/${actionModuleId}/employees/${employeeId}/instances?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
    ),

  bookTransactionForActionModule: async (
    actionModuleId: string
  ): Promise<CallResult<void>> => {
    return await doApiPost<null, void>(
      `/api/companies/current/actionmodules/${actionModuleId}/transactions/book`,
      null
    );
  },

  adminGetAllActionModules: (
    pagination: PaginationParameters
  ): Promise<CallResult<PaginationResult<ActionModuleAdminListResponse>>> =>
    doApiGet<PaginationResult<ActionModuleAdminListResponse>>(
      `/api/administration/actionmodules?${toQueryParameters(pagination)}`
    ),

  adminGetAction: (actionModuleId: string, companyId: number): Promise<CallResult<ActionModule>> =>
    doApiGet<ActionModule>(`/api/administration/companies/${companyId}/actionmodules/${actionModuleId}`),

  adminCreateAction: (companyId: number, actionData: ActionModuleRequest, validateOnly: boolean): Promise<CallResult<void>> =>
    doApiPost<ActionModuleRequest, void>(`/api/administration/companies/${companyId}/actionmodules?validateOnly=${validateOnly}`, actionData),

  adminDeleteAction: (companyId: number, actionModuleId: string): Promise<CallResult<void>> =>
    doApiDelete<void>(`/api/administration/companies/${companyId}/actionmodules/${actionModuleId}`),

  adminEditAction: (actionModuleId: number, companyId: number, actionData: ActionModuleRequest, validateOnly: boolean): Promise<CallResult<void>> =>
    doApiPatch<ActionModuleRequest, void>(`/api/administration/companies/${companyId}/actionmodules/${actionModuleId}?validateOnly=${validateOnly}`, actionData),
};